import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MultiStepWizardPage from '../../components/Wizard/MultiStepWizardPage';
import CertificateTemplateFillGroupStep from '../../components/Modal/steps/CertificateTemplateFillGroupStep';
import CertificateTemplateFillSchemaScreenStep from '../../components/Modal/steps/CertificateTemplateFillSchemaScreenStep';
import type { StepProps } from '../../components/Modal/MultiStepModal';
import { engineerService } from '../../services/engineerService';
import { isFilledSignatureValue } from '../../utils/certificateFieldValidation';
import { useToast } from '../../contexts/ToastContext';

function EngineerPdfCertReviewStep(_props: StepProps) {
  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Review</h3>
        <p className="step-form-subtitle">
          Submit this certificate to the office. Admin staff can review it before sending it to the client.
        </p>
      </div>
    </div>
  );
}

export default function EngineerPdfTemplateWizard({
  jobId,
  templateId,
  templateMeta,
  pdfWizard,
  onCancel,
}: {
  jobId: string;
  templateId: string;
  templateMeta: {
    templateLabel: string;
    schemaJson?: unknown;
  };
  pdfWizard: { screens: any[]; fields: any[] };
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data: job, isLoading } = useQuery({
    queryKey: ['engineer', 'job', jobId],
    queryFn: () => engineerService.getJob(jobId),
    enabled: !!jobId,
  });

  const initialData = useMemo(() => {
    const client = job?.client;
    const schemaObj =
      templateMeta.schemaJson && typeof templateMeta.schemaJson === 'object'
        ? (templateMeta.schemaJson as Record<string, unknown>)
        : {};
    const screensFromPdf = pdfWizard.screens?.length > 0 ? pdfWizard.screens : [];
    const screensFromSchema = Array.isArray((schemaObj as { screens?: unknown }).screens)
      ? ((schemaObj as { screens: any[] }).screens as any[])
      : [];
    const screens = screensFromPdf.length > 0 ? screensFromPdf : screensFromSchema;

    return {
      selectedTemplate: {
        fields: pdfWizard.fields,
        schemaJson: { ...schemaObj, screens },
        templateLabel: templateMeta.templateLabel,
      },
      filledData: {} as Record<string, unknown>,
      customerId: client?.id,
      customerName: client?.name || '',
      customerPhone: client?.phone || '',
      customerEmail: '',
      customerAddress: client?.address || job?.address || '',
      postcode: client?.postcode || job?.postcode || '',
      siteName: job?.site?.name || '',
      siteAddress: job?.site?.address || job?.address || '',
      sitePostcode: job?.site?.postcode || job?.postcode || '',
      jobId,
      templateId,
    };
  }, [job, jobId, templateId, pdfWizard, templateMeta]);

  const groupStepValidation = (groupName: string) => (d: any) => {
    const tmpl = d?.selectedTemplate;
    const fields: any[] = Array.isArray(tmpl?.fields) ? tmpl.fields : [];
    const filled: Record<string, any> = d.filledData && typeof d.filledData === 'object' ? d.filledData : {};

    const g = String(groupName || '').trim() || 'Ungrouped';
    for (const f of fields) {
      const gn = String(f?.groupName || '').trim() || 'Ungrouped';
      if (gn !== g) continue;
      if (!f?.required) continue;

      const key = String(f?.pdfFieldName || '').trim();
      if (!key) continue;

      const ft = String(f?.fieldType || 'text');
      const v = filled[key];

      if (ft === 'checkbox') {
        if (v !== true) return false;
        continue;
      }

      if (ft === 'signature') {
        if (!isFilledSignatureValue(v)) return false;
        continue;
      }

      if (!String(v ?? '').trim()) return false;
    }

    return true;
  };

  const schemaScreenValidation = (screenKey: string) => (d: any) => {
    const tmpl = d?.selectedTemplate;
    const schema = tmpl?.schemaJson;
    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    const screen = screens.find((s) => String(s?.key || '').trim() === String(screenKey || '').trim());
    if (!screen) return true;

    const filled: Record<string, any> = d.filledData && typeof d.filledData === 'object' ? d.filledData : {};
    const sections: any[] = Array.isArray(screen?.sections) ? screen.sections : [];
    for (const sec of sections) {
      const fs: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      for (const f of fs) {
        if (!f?.required) continue;
        const key = String(f?.pdfFieldName || '').trim();
        if (!key) continue;
        const ft = String(f?.fieldType || 'text');
        const v = filled[key];
        if (ft === 'checkbox') {
          if (v !== true) return false;
        } else if (ft === 'signature') {
          if (!isFilledSignatureValue(v)) return false;
        } else {
          if (!String(v ?? '').trim()) return false;
        }
      }
    }
    return true;
  };

  const getSchemaScreens = (data: any): any[] => {
    const tmpl = data?.selectedTemplate;
    const schema = tmpl?.schemaJson;
    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    return screens.filter((s) => String(s?.key || '').trim());
  };

  const getGroupNames = (data: any): string[] => {
    const tmpl = data?.selectedTemplate;
    const fields: any[] = Array.isArray(tmpl?.fields) ? tmpl.fields : [];

    const set = new Set<string>();
    for (const f of fields) {
      const g = String(f?.groupName || '').trim() || 'Ungrouped';
      set.add(g);
    }

    const arr = Array.from(set);
    arr.sort((a, b) => {
      if (a === 'Ungrouped' && b !== 'Ungrouped') return 1;
      if (b === 'Ungrouped' && a !== 'Ungrouped') return -1;
      return a.localeCompare(b);
    });
    return arr;
  };

  const stepsFactory = (data: any) => {
    const screens = getSchemaScreens(data);
    const groups = screens.length === 0 ? getGroupNames(data) : [];

    const steps: any[] = [];

    if (screens.length) {
      for (const s of screens) {
        const key = String(s?.key || '').trim();
        if (!key) continue;
        const title = String(s?.title || key);
        steps.push({
          id: `screen:${key}`,
          title,
          component: CertificateTemplateFillSchemaScreenStep,
          stepDataPatch: { activeScreenKey: key },
          validation: schemaScreenValidation(key),
        });
      }
    } else {
      for (const g of groups) {
        steps.push({
          id: `group:${g}`,
          title: g,
          component: CertificateTemplateFillGroupStep,
          stepDataPatch: { activeGroupName: g },
          validation: groupStepValidation(g),
        });
      }
    }

    steps.push({
      id: 'review',
      title: 'Review',
      component: EngineerPdfCertReviewStep,
      validation: () => true,
    });

    return steps;
  };

  if (isLoading || !job) {
    return <div className="px-4 py-6 text-sm text-slate-500">Loading job details…</div>;
  }

  return (
    <MultiStepWizardPage
      title={templateMeta.templateLabel}
      subtitle="On-site certificate"
      breadcrumb={
        <>
          Engineer <span className="mx-1 text-[#D1D5DB]">/</span> Job <span className="mx-1 text-[#D1D5DB]">/</span>{' '}
          Certificate
        </>
      }
      steps={stepsFactory}
      initialData={initialData}
      completeLabel="Submit certificate"
      nextLabel="Continue"
      onCancel={onCancel}
      onComplete={async (data: any) => {
        await engineerService.submitCertificate({
          jobId,
          templateId,
          filledData: data.filledData && typeof data.filledData === 'object' ? data.filledData : {},
          notes: data.notes,
        });
        showToast('Certificate submitted', 'success');
        navigate(`/engineer/jobs/${jobId}`);
      }}
    />
  );
}
